import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ApiClientError } from '../api/client';
import { AuthLayout } from '../components/AuthLayout';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { useAuth } from '../auth/AuthContext';

export function SignupPage() {
  const { signup } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setSuccess(null);
    setSubmitting(true);
    try {
      const result = await signup(email, password, displayName);
      setSuccess(result.message);
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.code === 'CONFLICT') {
          setError('このメールアドレスは既に登録されています。');
        } else if (err.code === 'VALIDATION_ERROR') {
          setError(err.message);
        } else {
          setError(err.message);
        }
      } else {
        setError('登録申請に失敗しました。時間を置いて再度お試しください。');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout
      title="登録申請"
      subtitle="管理者の承認後にログインできます"
      footer={
        <>
          すでにアカウントをお持ちの方は <Link to="/login">ログイン</Link>
        </>
      }
    >
      {success ? (
        <div
          role="status"
          style={{
            background: 'rgba(34, 197, 94, 0.12)',
            border: '0.5px solid rgba(34, 197, 94, 0.4)',
            color: 'var(--color-success-text)',
            borderRadius: 'var(--radius-md)',
            padding: '12px 14px',
            fontSize: '12px',
          }}
        >
          {success}
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <Input
            label="メールアドレス"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
          <Input
            label="表示名"
            type="text"
            autoComplete="nickname"
            required
            maxLength={100}
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
          />
          <Input
            label="パスワード"
            type="password"
            autoComplete="new-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            hint="8文字以上、英大小・数字・記号のうち3種以上を含む"
          />
          {error && (
            <div
              role="alert"
              style={{
                background: 'rgba(239, 68, 68, 0.12)',
                border: '0.5px solid rgba(239, 68, 68, 0.4)',
                color: 'var(--color-error-text)',
                borderRadius: 'var(--radius-md)',
                padding: '10px 12px',
                fontSize: '11px',
                marginBottom: '14px',
              }}
            >
              {error}
            </div>
          )}
          <Button type="submit" disabled={submitting}>
            {submitting ? '送信中…' : '申請する'}
          </Button>

          {/* 設計書 §3.4.1: Google による新規登録は仕様にない。
              「ログイン画面に Google ボタンがあるのに Signup には無いのはなぜ?」
              というユーザーの疑問を解消するための注記。
              ログイン画面の Google ボタン経由で初回認証 → メール一致 +
              既存パスワードで連携確定、という 2要素フロー(CLAUDE.md
              「Google OAuth 2要素フロー拡張」)を文章で案内する。 */}
          <p
            style={{
              margin: '18px 0 0',
              padding: '10px 12px',
              background: 'rgba(167, 139, 250, 0.08)',
              border: '0.5px solid rgba(167, 139, 250, 0.25)',
              borderRadius: 'var(--radius-md)',
              fontSize: '11px',
              lineHeight: 1.6,
              color: 'var(--color-text-secondary)',
            }}
          >
            Google アカウントでログインしたい場合は、まずこの画面でメール
            アドレスとパスワードで登録申請を完了し、管理者の承認を受けてから
            ログイン画面の「Google でログイン」を初回利用してください。
            登録時のメールアドレスと一致する Google アカウントで認証 →
            登録時のパスワードを再入力すると自動的に連携されます。
          </p>
        </form>
      )}
    </AuthLayout>
  );
}
